import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { HELP_DESK_STATUSES, HELP_DESK_TICKET_BADGES } from '@ubeya/shared/constants/helpDesk';
import { Button, TimePicker as BaseTimePicker } from '@ubeya/shared-web/components';
import { DatePicker as BaseDatePicker } from '@ubeya/shared-web/components/DatePicker';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import DropDownContext from '@ubeya/shared-web/contexts/DropDownContext';
import Tooltip from '@ubeya/shared-web/components/Tooltip/Tooltip';
import { ReactComponent as BaseSnoozeIcon } from '@ubeya/shared-web/assets/help-desk/snooze.svg';
import { ReactComponent as BaseArchiveIcon } from '@ubeya/shared-web/assets/archive.svg';
import { ReactComponent as BaseArrowsCircleIcon } from '@ubeya/shared-web/assets/arrows-circle.svg';
import { ReactComponent as RetryIcon } from '@ubeya/shared-web/assets/retry.svg';
import { ReactComponent as ArrowLeftIcon } from '@ubeya/shared-web/assets/arrow-left.svg';
import { ReactComponent as BaseChevronRightIcon } from '@ubeya/shared-web/assets/chevron-right.svg';
import { ReactComponent as BaseResolvedCheckIcon } from '@ubeya/shared-web/assets/circle-check-icon.svg';
import { ReactComponent as BasePendingIcon } from '@ubeya/shared-web/assets/help-desk/pending.svg';
import { ReactComponent as BaseChatIcon } from '@ubeya/shared-web/assets/chat.svg';
import { ReactComponent as BaseUnreadIcon } from '@ubeya/shared-web/assets/unread.svg';
import { isTicketResolvedPending } from '../helpDeskUtils';

// region: Constants

const MENU_STEPS = {
  MAIN: 'MAIN',
  SNOOZE: 'SNOOZE'
};

const DEFAULT_SNOOZE_TIME = '09:00:00';

const SNOOZE_OPTIONS = [
  {
    labelKey: 'help_desk_snooze_1h',
    getDate: () => moment().add(1, 'hour')
  },
  {
    labelKey: 'help_desk_snooze_tomorrow',
    getDate: () => moment().add(1, 'day').startOf('day').hour(9)
  },
  {
    labelKey: 'help_desk_snooze_next_week',
    getDate: () => moment().add(1, 'week').startOf('day').hour(9)
  },
  {
    labelKey: 'help_desk_snooze_next_month',
    getDate: () => moment().add(1, 'month').startOf('day').hour(9)
  }
];

// region: Styled components

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverBackground};
  }

  ${({ $isActive, theme }) => $isActive && `background: ${theme.colors.hoverBackground};`}

  ${({ $disabled }) =>
    $disabled &&
    `
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background: transparent;
    }
  `}

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ResolvedCheckIcon = styled(BaseResolvedCheckIcon)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.grey4};

  path {
    stroke: currentColor;
  }
`;

const MenuItemIconLeft = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuItemIconRight = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding-left: 16px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuItemLabel = styled.span`
  flex: 1;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black};
`;

const MenuItemDate = styled.span`
  margin-left: auto;
  padding-left: 20px;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
`;

const SnoozeIcon = styled(BaseSnoozeIcon)`
  color: ${({ theme }) => theme.colors.grey4};

  path,
  line,
  circle {
    stroke: currentColor;
  }
`;

const ChevronRightIcon = styled(BaseChevronRightIcon)`
  color: ${({ theme }) => theme.colors.grey4};

  path {
    fill: currentColor;
  }
`;

const ArrowsCircleIcon = styled(BaseArrowsCircleIcon)`
  color: ${({ theme }) => theme.colors.grey4};
  margin: 0 2px;

  /* && outranks the svg { 20px } rules on MenuItem / MenuItemIconLeft */
  && {
    width: 16px;
    height: 16px;
  }

  path {
    stroke: currentColor;
  }
`;

const PendingIcon = styled(BasePendingIcon)`
  color: ${({ theme }) => theme.colors.grey4};
`;

const ChatIcon = styled(BaseChatIcon)`
  color: ${({ theme }) => theme.colors.grey4};

  path[stroke] {
    stroke: currentColor;
  }

  path[fill] {
    fill: currentColor;
  }
`;

const UnreadIcon = styled(BaseUnreadIcon)`
  color: ${({ theme }) => theme.colors.grey4};

  path[stroke] {
    stroke: currentColor;
  }
`;

const ArchiveIcon = styled(BaseArchiveIcon)`
  color: ${({ theme }) => theme.colors.grey4};

  path {
    fill: currentColor;
  }
`;

const SubMenuHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  margin-bottom: 4px;
`;

const BackButton = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  padding: 2px;

  &:hover {
    background: ${({ theme }) => theme.colors.grey1};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CustomSnoozeLayout = styled.div`
  display: flex;
`;

const CustomSnoozeLeft = styled.div`
  min-width: 310px;
  margin-left: -10px;
  margin-top: -10px;
`;

const SnoozeButton = styled(Button)`
  width: 100%;
`;

const DatePicker = styled(BaseDatePicker)`
  padding: 0;

  .DayPicker,
  .DayPicker_1,
  .DayPicker__withBorder,
  .DayPicker__withBorder_3,
  .DayPicker__horizontal,
  .DayPicker__horizontal_2,
  .CalendarMonth,
  .CalendarMonth_1,
  .CalendarMonthGrid,
  .CalendarMonthGrid_1 {
    border: none;
    box-shadow: none;
  }

  .DayPicker {
    padding: 0;
  }
`;

const CustomSnoozeRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-left: 10px;
  border-left: 1px solid ${({ theme }) => theme.colors.grey2};
`;

const TimePicker = styled(BaseTimePicker)`
  margin: 0 10px 0;
`;

const ApplyRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 10px 0px;
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

// region: Component

const HelpDeskActionsMenu = ({
  ticket,
  onResolve,
  resolveDisabled = false,
  onSnooze,
  onUnsnooze,
  onUnresolve,
  onCloseTicketRequest,
  onReopen,
  onTogglePending,
  isUnread = false,
  onToggleRead,
  onCustomPickerVisibleChange
}) => {
  const { t } = useContext(ConfigContext);
  const { closeMenu } = useContext(DropDownContext);

  const isSnoozed = ticket.status === HELP_DESK_STATUSES.SNOOZED;
  const isClosed = ticket.status === HELP_DESK_STATUSES.CLOSED;
  const isResolvedPending = isTicketResolvedPending(ticket);
  const isPending = ticket.badge === HELP_DESK_TICKET_BADGES.PENDING;

  // DropDown unmounts this component when the menu closes, so each open starts fresh on MAIN.
  const [menuStep, setMenuStep] = useState(MENU_STEPS.MAIN);
  const [isCustomPickerVisible, setIsCustomPickerVisible] = useState(false);

  // Custom snooze state
  const [snoozeDate, setSnoozeDate] = useState(() => moment().add(1, 'day').toISOString());
  const [snoozeTime, setSnoozeTime] = useState(DEFAULT_SNOOZE_TIME);

  // Signal width preference upward so parent can widen the DropDown panel.
  useEffect(() => {
    onCustomPickerVisibleChange?.(isCustomPickerVisible);
  }, [isCustomPickerVisible, onCustomPickerVisibleChange]);

  // On unmount (menu close), reset the parent flag so the next open starts with the narrow panel.
  useEffect(
    () => () => {
      onCustomPickerVisibleChange?.(false);
    },
    [onCustomPickerVisibleChange]
  );

  const handleSnoozeOptionClick = useCallback(
    (date) => {
      onSnooze(date.toISOString());
      closeMenu?.();
    },
    [onSnooze, closeMenu]
  );

  const handleUnsnooze = useCallback(() => {
    onUnsnooze();
    closeMenu?.();
  }, [onUnsnooze, closeMenu]);

  const handleReopen = useCallback(() => {
    onReopen();
    closeMenu?.();
  }, [onReopen, closeMenu]);

  const handleUnresolve = useCallback(() => {
    onUnresolve();
    closeMenu?.();
  }, [onUnresolve, closeMenu]);

  const handleCloseTicket = useCallback(() => {
    closeMenu?.();
    onCloseTicketRequest();
  }, [closeMenu, onCloseTicketRequest]);

  const handleTogglePending = useCallback(() => {
    onTogglePending();
    closeMenu?.();
  }, [onTogglePending, closeMenu]);

  const handleToggleRead = useCallback(() => {
    onToggleRead();
    closeMenu?.();
  }, [onToggleRead, closeMenu]);

  const handleResolveClick = useCallback(() => {
    if (resolveDisabled) return;
    onResolve();
    closeMenu?.();
  }, [resolveDisabled, onResolve, closeMenu]);

  const handleCustomSnoozeOpen = useCallback(() => {
    setSnoozeDate(moment().add(1, 'day').toISOString());
    setSnoozeTime(DEFAULT_SNOOZE_TIME);
    setIsCustomPickerVisible(true);
  }, []);

  const handleSnoozeTimeChange = useCallback((val) => {
    if (val) setSnoozeTime(val);
  }, []);

  const customSnoozeMoment = useMemo(() => {
    const [h, m] = (snoozeTime || DEFAULT_SNOOZE_TIME).split(':');
    return moment(snoozeDate).hour(Number(h)).minute(Number(m)).second(0);
  }, [snoozeDate, snoozeTime]);
  const isCustomSnoozeInPast = !customSnoozeMoment.isAfter(moment());

  const handleCustomSnoozeConfirm = useCallback(() => {
    if (!isCustomSnoozeInPast) {
      onSnooze(customSnoozeMoment.toISOString());
      closeMenu?.();
    }
  }, [customSnoozeMoment, isCustomSnoozeInPast, onSnooze, closeMenu]);

  // Compute snooze preview dates
  const snoozeDates = useMemo(
    () =>
      SNOOZE_OPTIONS.map((opt) => ({
        ...opt,
        computedDate: opt.getDate()
      })),
    []
  );

  // region: Render menu content

  const renderMainMenu = () => (
    <>
      {onToggleRead && (
        <MenuItem onClick={handleToggleRead}>
          <MenuItemIconLeft>{isUnread ? <ChatIcon /> : <UnreadIcon />}</MenuItemIconLeft>
          <MenuItemLabel>{t(isUnread ? 'help_desk_mark_as_read' : 'help_desk_mark_as_unread')}</MenuItemLabel>
        </MenuItem>
      )}
      {!isClosed && !isResolvedPending && onTogglePending && (
        <MenuItem onClick={handleTogglePending}>
          <MenuItemIconLeft>
            <PendingIcon />
          </MenuItemIconLeft>
          <MenuItemLabel>{isPending ? t('help_desk_unmark_as_pending') : t('help_desk_mark_as_pending')}</MenuItemLabel>
        </MenuItem>
      )}
      {!isClosed && onResolve && !isResolvedPending && (
        <MenuItem onClick={handleResolveClick} $disabled={resolveDisabled}>
          <MenuItemIconLeft>
            <ResolvedCheckIcon />
          </MenuItemIconLeft>
          <MenuItemLabel>{t('help_desk_mark_resolved')}</MenuItemLabel>
        </MenuItem>
      )}
      {!isClosed && !isResolvedPending && (
        <MenuItem onClick={() => setMenuStep(MENU_STEPS.SNOOZE)}>
          <MenuItemIconLeft>
            <SnoozeIcon />
          </MenuItemIconLeft>
          <MenuItemLabel>{t('help_desk_snooze')}</MenuItemLabel>
          <MenuItemIconRight>
            <ChevronRightIcon />
          </MenuItemIconRight>
        </MenuItem>
      )}
      {isResolvedPending && (
        <MenuItem onClick={handleUnresolve}>
          <MenuItemIconLeft>
            <ArrowsCircleIcon />
          </MenuItemIconLeft>
          <MenuItemLabel>{t('help_desk_unresolve')}</MenuItemLabel>
        </MenuItem>
      )}
      {isClosed ? (
        <MenuItem onClick={handleReopen}>
          <MenuItemIconLeft>
            <ArrowsCircleIcon />
          </MenuItemIconLeft>
          <MenuItemLabel>{t('help_desk_reopen_ticket')}</MenuItemLabel>
        </MenuItem>
      ) : (
        <MenuItem onClick={handleCloseTicket}>
          <MenuItemIconLeft>
            <ArchiveIcon />
          </MenuItemIconLeft>
          <MenuItemLabel>{t('help_desk_close_ticket')}</MenuItemLabel>
        </MenuItem>
      )}
    </>
  );

  const renderSnoozeMenu = ({
    onBack = () => {
      setMenuStep(MENU_STEPS.MAIN);
      setIsCustomPickerVisible(false);
    },
    onSnoozeOptionClick = handleSnoozeOptionClick,
    onCustomClick = handleCustomSnoozeOpen,
    isCustomActive = false
  } = {}) => (
    <>
      <SubMenuHeader>
        <BackButton onClick={onBack}>
          <ArrowLeftIcon />
        </BackButton>
        {t('help_desk_snooze')}
      </SubMenuHeader>

      {isSnoozed && (
        <MenuItem onClick={handleUnsnooze}>
          <MenuItemIconLeft>
            <RetryIcon />
          </MenuItemIconLeft>
          <MenuItemLabel>{t('help_desk_unsnooze')}</MenuItemLabel>
        </MenuItem>
      )}

      {snoozeDates.map((opt) => (
        <MenuItem key={opt.labelKey} onClick={() => onSnoozeOptionClick(opt.computedDate)}>
          <MenuItemLabel>{t(opt.labelKey)}</MenuItemLabel>
          <MenuItemDate>{opt.computedDate.format('ddd, MMM D, h:mm A')}</MenuItemDate>
        </MenuItem>
      ))}

      <MenuItem onClick={onCustomClick} $isActive={isCustomActive}>
        <MenuItemLabel>{t('help_desk_snooze_custom')}</MenuItemLabel>
      </MenuItem>
    </>
  );

  const renderSnoozeWithCustomPicker = () => (
    <CustomSnoozeLayout>
      <CustomSnoozeLeft>
        <DatePicker
          value={snoozeDate}
          onChange={setSnoozeDate}
          isOutsideRange={(day) => day.isBefore(moment(), 'day')}
          renderSelects={false}
        />
        <TimePicker timeOnly value={snoozeTime} onChange={handleSnoozeTimeChange} />
        <ApplyRow>
          <Tooltip body={isCustomSnoozeInPast ? t('invalidInput') : ''}>
            <SnoozeButton narrow primary onClick={handleCustomSnoozeConfirm} disabled={isCustomSnoozeInPast}>
              {t('help_desk_snooze_apply')}
            </SnoozeButton>
          </Tooltip>
        </ApplyRow>
      </CustomSnoozeLeft>

      <CustomSnoozeRight>
        {renderSnoozeMenu({
          onSnoozeOptionClick: handleSnoozeOptionClick,
          onCustomClick: () => {},
          isCustomActive: true
        })}
      </CustomSnoozeRight>
    </CustomSnoozeLayout>
  );

  return (
    <MenuContainer onClick={(e) => e.stopPropagation()}>
      {menuStep === MENU_STEPS.MAIN && renderMainMenu()}
      {menuStep === MENU_STEPS.SNOOZE && (isCustomPickerVisible ? renderSnoozeWithCustomPicker() : renderSnoozeMenu())}
    </MenuContainer>
  );
};

export default HelpDeskActionsMenu;
